import { Component, OnDestroy, OnInit } from '@angular/core';
import { DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Observable, firstValueFrom } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { faArrowLeft, faCalendar, faClock, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { NgSelectModule } from '@ng-select/ng-select';
import { RetrieversService, RetrieverExecution } from '../../services/retrievers.service';
import { ProcessingsService, ProcessingExecution } from '../../services/processings.service';
import { TenantService } from '../../services/tenant.service';
import { AlertService } from '../../components/alert-service/alert-service';
import { RetrieverResultViewComponent } from '../../components/retriever-result-view/retriever-result-view.component';
import { CodeViewerComponent } from '../../components/code-viewer/code-viewer.component';
import { SimpleSimulationType } from '../../components/simulation-executions-list/simulation-executions-list.component';
import { ArchivesService } from '../../services/archives.service';

@Component({
  selector: 'app-simulation-execution-detail',
  standalone: true,
  imports: [DatePipe, FormsModule, NgSelectModule, FontAwesomeModule, TranslateModule, RetrieverResultViewComponent, CodeViewerComponent],
  templateUrl: './simulation-execution-detail.component.html',
  styleUrls: ['./simulation-execution-detail.component.scss'],
})
export class SimulationExecutionDetailComponent implements OnInit, OnDestroy {
  readonly icons = { faArrowLeft, faCalendar, faClock, faSpinner };
  simulationType: SimpleSimulationType = 'processing';
  simulationId = '';
  executionId = '';
  execution: RetrieverExecution | ProcessingExecution | null = null;
  loading = true;
  resultNumber: number | null = null;
  processingFiles: string[] = [];
  selectedProcessingFile: string | null = null;
  processingInput: any = null;
  processingOutput: any = null;
  loadingProcessingInput = false;

  private readonly POLLING_INTERVAL_MS = 5000;
  private pollHandle: ReturnType<typeof setInterval> | null = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private retrieversService: RetrieversService,
    private processingsService: ProcessingsService,
    private archivesService: ArchivesService,
    private tenantService: TenantService,
    private alert: AlertService,
    private translate: TranslateService,
  ) {}

  ngOnInit(): void {
    this.simulationType = this.route.snapshot.data['simulationType'];
    this.simulationId = this.route.snapshot.paramMap.get('id') || '';
    this.executionId = this.route.snapshot.paramMap.get('executionId') || '';
    const resultNumber = Number(this.route.snapshot.queryParamMap.get('resultNumber'));
    this.resultNumber = Number.isFinite(resultNumber) && resultNumber > 0 ? resultNumber : null;

    const request: Observable<RetrieverExecution | ProcessingExecution> = this.simulationType === 'retriever'
      ? this.retrieversService.getExecution(this.simulationId, this.executionId)
      : this.processingsService.getExecution(this.simulationId, this.executionId);

    request.subscribe({
      next: execution => {
        this.execution = execution;
        this.loading = false;
        if (this.simulationType === 'processing') {
          this.updateProcessingFiles(execution as ProcessingExecution);
        }
        this.syncPolling();
      },
      error: err => {
        this.loading = false;
        this.alert.showError(err);
        this.goBack();
      },
    });
  }

  ngOnDestroy(): void {
    this.stopPolling();
  }

  async onProcessingFileChange(fileName: string | null): Promise<void> {
    this.selectedProcessingFile = fileName;
    this.processingOutput = fileName && this.execution?.result
      ? this.execution.result[fileName]
      : null;
    this.processingInput = null;
    if (!fileName) return;

    const processingExecution = this.execution as ProcessingExecution;
    const snapshotInput = processingExecution.config?.input_data?.[fileName];
    if (snapshotInput !== undefined) {
      this.processingInput = snapshotInput;
      return;
    }

    await this.loadLegacyProcessingInput(fileName);
  }

  private updateProcessingFiles(execution: ProcessingExecution): void {
    this.processingFiles = execution.result && typeof execution.result === 'object'
      ? Object.keys(execution.result)
      : [];

    if (this.selectedProcessingFile && this.processingFiles.includes(this.selectedProcessingFile)) {
      this.processingOutput = execution.result[this.selectedProcessingFile];
      return;
    }
    this.onProcessingFileChange(this.processingFiles[0] || null);
  }

  private syncPolling(): void {
    if (this.isStillRunning) {
      this.startPolling();
    } else {
      this.stopPolling();
    }
  }

  private startPolling(): void {
    if (this.pollHandle) return;
    this.pollHandle = setInterval(() => this.refreshExecution(), this.POLLING_INTERVAL_MS);
  }

  private stopPolling(): void {
    if (this.pollHandle) {
      clearInterval(this.pollHandle);
      this.pollHandle = null;
    }
  }

  private refreshExecution(): void {
    this.processingsService.getExecution(this.simulationId, this.executionId).subscribe({
      next: execution => {
        this.execution = execution;
        this.updateProcessingFiles(execution);
        this.syncPolling();
      },
      error: () => this.stopPolling(),
    });
  }

  private async loadLegacyProcessingInput(fileName: string): Promise<void> {
    this.loadingProcessingInput = true;
    try {
      const [processing, archivesResponse] = await Promise.all([
        firstValueFrom(this.processingsService.getById(this.simulationId)),
        firstValueFrom(this.archivesService.list(undefined, undefined, 1, 9999, 'json')),
      ]);
      const allowedIds = new Set((processing.input_archive_ids || []).map(String));
      const archive = (archivesResponse.archives || []).find(item =>
        allowedIds.has(String(item.id)) && item.name === fileName
      );
      if (!archive) return;

      const blob = await firstValueFrom(this.archivesService.download(archive.id));
      const text = await blob.text();
      try {
        this.processingInput = JSON.parse(text);
      } catch {
        this.processingInput = text;
      }
    } catch {
      this.processingInput = null;
    } finally {
      this.loadingProcessingInput = false;
    }
  }

  get isStillRunning(): boolean {
    return this.simulationType === 'processing'
      && (this.execution as ProcessingExecution | null)?.config?.['status'] === 'running';
  }

  get title(): string {
    if (this.resultNumber !== null) {
      return this.translate.instant('INTERACTIONS.RESULTS.RESULT_NAME', { number: this.resultNumber });
    }
    return this.translate.instant('PROCESSINGS.TABS.RESULT');
  }

  goBack(): void {
    const segment = this.simulationType === 'retriever' ? 'retrievers' : 'processings';
    this.router.navigate(this.tenantService.buildRoute([segment, this.simulationId]), {
      queryParams: { tab: 'result' },
    });
  }
}
